import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { API, apiError, formatINR, formatDate } from "../lib/api";
import { ArrowLeft, Plus, X, BookOpen, Edit2, Trash2, User } from "lucide-react";
import { toast } from "sonner";

const EMPTY_PAY = { amount: "", mode: "Cash", date: new Date().toISOString().slice(0, 10), notes: "" };

export default function CustomerLedger() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_PAY);

  const load = useCallback(async () => {
    try { const r = await API.get(`/customers/${id}/ledger`); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY_PAY, amount: data?.customer?.balance > 0 ? data.customer.balance : "" }); setModal("new"); };
  const openEdit = (t) => {
    setForm({
      amount: t.credit, mode: t.mode || "Cash", date: t.date,
      notes: (t.description || "").replace(/^Payment received \([^)]+\)\s*/, ""),
    });
    setModal(t);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, amount: parseFloat(form.amount) };
      if (modal === "new") await API.post(`/customers/${id}/payments`, body);
      else await API.put(`/customer_transactions/${modal.id}`, body);
      toast.success(modal === "new" ? "Payment received" : "Payment updated");
      setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const delTxn = async (t) => {
    if (t.type === "SALE_CREDIT") {
      toast.error("Delete the linked sale (Reports → Sales) to remove a credit-sale entry.");
      return;
    }
    if (!window.confirm("Delete this payment?")) return;
    try { await API.delete(`/customer_transactions/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;
  const { customer, entries } = data;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="customer-ledger-page">
      <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#002FA7]">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Customers
      </Link>

      <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-wrap items-start gap-6 justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#1D4ED8] text-white flex items-center justify-center">
            <User className="w-7 h-7" />
          </div>
          <div>
            <div className="overline mb-1">Customer Ledger</div>
            <h1 className="font-display text-3xl font-bold">{customer.name}</h1>
            <div className="text-sm text-slate-500 mt-1 space-x-3">
              {customer.phone && <span>{customer.phone}</span>}
              {customer.email && <span>· {customer.email}</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="overline">Balance</div>
          <div className={`font-display text-3xl font-bold ${customer.balance > 0 ? "text-red-600" : customer.balance < 0 ? "text-emerald-600" : "text-slate-400"}`}>
            {formatINR(Math.abs(customer.balance))}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {customer.balance > 0 ? "Customer owes you" : customer.balance < 0 ? "Advance paid" : "Settled"}
          </div>
          <button onClick={openNew} className="btn-primary mt-3" data-testid="receive-payment-btn"><Plus className="w-4 h-4" /> Receive Payment</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 font-display font-semibold flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> Transaction History
        </div>
        {entries.length === 0 ? (
          <div className="p-10 text-sm text-slate-500 text-center">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr>
                <th>Date</th><th>Type</th><th>Description</th>
                <th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Balance</th>
                <th className="text-right">Actions</th>
              </tr></thead>
              <tbody>
                {entries.map((t, i) => (
                  <tr key={t.id || i}>
                    <td className="text-sm">{formatDate(t.date)}</td>
                    <td><span className={`tag ${t.type === "SALE_CREDIT" ? "tag-warning" : t.type === "PAYMENT" ? "tag-success" : "tag-muted"}`}>{t.type}</span></td>
                    <td className="text-slate-600">
                      {t.description}
                      {t.ref && t.type === "SALE_CREDIT" && (
                        <a href="#" onClick={(e) => { e.preventDefault(); window.open(`/invoice/${t.ref_sale_id || ""}`, "_blank"); }} className="ml-2 text-xs text-[#002FA7] hover:underline">View bill →</a>
                      )}
                    </td>
                    <td className="text-right font-medium">{t.debit > 0 ? formatINR(t.debit) : ""}</td>
                    <td className="text-right font-medium text-emerald-700">{t.credit > 0 ? formatINR(t.credit) : ""}</td>
                    <td className="text-right font-semibold">{formatINR(t.balance)}</td>
                    <td className="text-right">
                      {t.type === "PAYMENT" && t.id && (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => delTxn(t)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">{modal === "new" ? "Receive Payment" : "Edit Payment"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <L label="Amount Received (₹) *">
                <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="inp" data-testid="cust-payment-amount" />
              </L>
              <L label="Mode">
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="inp">
                  {["Cash", "UPI", "Card", "Cheque", "Bank Transfer"].map((m) => <option key={m}>{m}</option>)}
                </select>
              </L>
              <L label="Date"><input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="inp" /></L>
              <L label="Notes"><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="inp" placeholder="Reference / cheque no" /></L>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-cust-payment-btn">{modal === "new" ? "Receive" : "Update"}</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}
function L({ label, children }) { return <label className="block"><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
